import React, {useEffect, useState} from 'react';
import classes from './ScanAnalysisProgress.module.scss';

const STAGES = [
  'Uploading image...',
  'Detecting receipt...',
  'Extracting items...',
  'Calculating totals...',
  'Finishing up...',
];
const CIRCUMFERENCE = 2 * Math.PI * 58;

interface ScanAnalysisProgressProps {
  complete: boolean;
  onComplete: () => void;
}

const ScanAnalysisProgress = ({complete, onComplete}: ScanAnalysisProgressProps) => {
  const [percent, setPercent] = useState(0);
  const finished = complete && percent === 100;

  useEffect(() => {
    if (complete) return;
    const startedAt = performance.now();
    // The scan API returns once: these stages estimate the entire request, not backend events.
    const timer = window.setInterval(() => {
      const elapsed = performance.now() - startedAt;
      const estimate = Math.min(95, Math.floor(100 * (1 - Math.exp(-elapsed / 12000))));
      setPercent(previous => Math.max(previous, estimate));
      if (estimate === 95) window.clearInterval(timer);
    }, 250);
    return () => window.clearInterval(timer);
  }, [complete]);

  useEffect(() => {
    if (!complete || finished) return;
    const timer = window.setInterval(() => {
      setPercent(previous => Math.min(100, previous + 5));
    }, 20);
    return () => window.clearInterval(timer);
  }, [complete, finished]);

  useEffect(() => {
    if (!finished) return;
    // Keep 100% visible beyond the ring's 250ms transition before opening review.
    const timer = window.setTimeout(onComplete, 350);
    return () => window.clearTimeout(timer);
  }, [finished, onComplete]);

  const currentStage = finished ? STAGES.length : Math.min(STAGES.length - 1, Math.floor(percent / 20));

  return (
    <section className={classes.analysis} aria-label={finished ? 'Receipt scan complete' : 'Receipt scan estimate'}>
      <div className={classes.ring} role="progressbar" aria-label={finished ? 'Receipt scanning complete' : 'Estimated receipt scanning progress'}
        aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}
        aria-valuetext={finished ? '100%; scan complete; receipt ready' : `${percent}% estimated; waiting for receipt scan`}>
        <svg viewBox="0 0 132 132" aria-hidden="true" focusable="false">
          <circle className={classes.track} cx="66" cy="66" r="58"/>
          <circle className={classes.arc} cx="66" cy="66" r="58"
            strokeDasharray={CIRCUMFERENCE} strokeDashoffset={CIRCUMFERENCE * (1 - percent / 100)}
            opacity={percent === 0 ? 0 : 1}/>
        </svg>
        <span aria-hidden="true">{percent}%</span>
      </div>
      <p className={classes.estimate}>{finished ? 'Scan complete' : 'Estimated progress'}</p>
      <h2 className={classes.heading} aria-live="polite" aria-atomic="true">{finished ? 'Receipt ready' : STAGES[currentStage]}</h2>
      <p className={classes.subtitle}>{finished ? 'Opening review…' : 'Please keep the app open'}</p>
      <ol className={classes.stages} aria-label={finished ? 'Completed scan stages' : 'Estimated scan stages'}>
        {STAGES.map((stage, index) => (
          <li key={stage}
            className={index < currentStage ? classes.completed : index === currentStage ? classes.current : classes.upcoming}
            aria-current={index === currentStage ? 'step' : undefined}>
            <span className={classes.badge} aria-hidden="true">
              {index < currentStage ? (
                <svg viewBox="0 0 16 16" focusable="false"><path d="m4 8 2.5 2.5L12 5"/></svg>
              ) : index + 1}
            </span>
            <span>{stage}</span>
          </li>
        ))}
      </ol>
    </section>
  );
};

export default ScanAnalysisProgress;
